import { useState } from "react";
import { RECON_LABELS, compareBankInvoice, portalLabel } from "../../services/payrollFinanceCalculator";
import { createWithdrawal, importWithdrawals, rejectWithdrawal, withdrawalDocId } from "../../services/payrollFinanceService";
import { parseBankSheet } from "../../services/payrollFinanceUpload";
import { PortalSelect, RECON_TONE, dateLabel, money, todayIso } from "./shared";

const NEW_EMPLOYEE = "__new__";

const emptyForm = (portal) => ({
  portal: portal === "ALL" ? "ALM" : portal, date: todayIso(), time: "", bankTxnId: "", amount: "", ratePercent: "1.9", note: "",
  employeeId: NEW_EMPLOYEE, employeeName: "", employeeCnic: "", employeeBank: "", employeeAccount: "", employeePhone: ""
});

export default function WithdrawalsTab({ data, position, portal, reload, notify }) {
  const [mode, setMode] = useState(""); // "", "add", "upload"
  const [form, setForm] = useState(() => emptyForm(portal));
  const [uploadPortal, setUploadPortal] = useState(portal === "ALL" ? "ALM" : portal);
  const [parsed, setParsed] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (field) => (e) => setForm((current) => ({ ...current, [field]: e.target.value }));

  const pickEmployee = (e) => {
    const id = e.target.value;
    const known = data.employees.find((employee) => employee.id === id);
    setForm((current) => ({
      ...current, employeeId: id,
      employeeName: known?.name || "", employeeCnic: known?.cnic || "", employeeBank: known?.bank || "", employeeAccount: known?.account || "", employeePhone: known?.phone || ""
    }));
  };

  const submit = async (event) => {
    event.preventDefault();
    try {
      await createWithdrawal({ ...form, monthlyRate: Number(form.ratePercent) / 100 });
      notify("Withdrawal recorded.");
      setForm(emptyForm(portal)); setMode("");
      await reload();
    } catch (err) { notify(err.message || "Could not record this withdrawal.", true); }
  };

  const chooseFile = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      setParsed(parseBankSheet(await file.arrayBuffer()));
    } catch (err) {
      setParsed(null); notify(err.message, true);
    }
    event.target.value = "";
  };

  const importNow = async () => {
    setBusy(true);
    try {
      const result = await importWithdrawals({ portal: uploadPortal, rows: parsed.rows });
      notify(`${result.imported} withdrawal(s) imported to ${portalLabel(uploadPortal)}; ${result.skipped} already recorded and skipped.`);
      setParsed(null); setMode("");
      await reload();
    } catch (err) { notify(err.message || "Import failed.", true); }
    finally { setBusy(false); }
  };

  const reject = async (row) => {
    const reason = window.prompt(`Reason for rejecting the ${money(row.principal)} withdrawal to ${row.withdrawal.employeeName}:`);
    if (reason === null) return;
    try { await rejectWithdrawal(row.withdrawal, reason); notify("Withdrawal rejected."); await reload(); }
    catch (err) { notify(err.message || "Could not reject.", true); }
  };

  const existingIds = new Set(data.withdrawals.map((w) => w.id));
  const previewRows = parsed ? parsed.rows.map((row) => {
    const duplicate = existingIds.has(withdrawalDocId(row.bankTxnId));
    const check = row.bankInvoice ? compareBankInvoice({ ...row, id: row.bankTxnId }) : null;
    return { row, duplicate, check };
  }) : [];
  const newRows = previewRows.filter((item) => !item.duplicate);

  return (
    <>
      <section className="panel">
        <div className="panel-header">
          <h2 className="panel-title">Withdrawals</h2>
          <div className="button-row">
            <button type="button" className="btn btn-primary" onClick={() => setMode(mode === "add" ? "" : "add")}>Record a Withdrawal</button>
            <button type="button" className="btn btn-muted" onClick={() => setMode(mode === "upload" ? "" : "upload")}>Upload Bank Sheet</button>
          </div>
        </div>

        {mode === "add" && (
          <form className="panel-body" onSubmit={submit}>
            <div className="form-grid">
              <PortalSelect value={form.portal} onChange={(value) => setForm({ ...form, portal: value })} />
              <div className="field"><label>Withdrawal date</label><input className="input" type="date" value={form.date} onChange={set("date")} /></div>
              <div className="field"><label>Time</label><input className="input" type="time" value={form.time} onChange={set("time")} /></div>
              <div className="field"><label>Amount (PKR)</label><input className="input" type="number" min="1" step="0.01" value={form.amount} onChange={set("amount")} /></div>
              <div className="field"><label>Bank transaction ID</label><input className="input" value={form.bankTxnId} onChange={set("bankTxnId")} /></div>
              <div className="field"><label>Monthly rate (%)</label><input className="input" type="number" step="0.01" value={form.ratePercent} onChange={set("ratePercent")} /></div>
              <div className="field"><label>Transferred to employee</label>
                <select className="input" value={form.employeeId} onChange={pickEmployee}>
                  <option value={NEW_EMPLOYEE}>New employee…</option>
                  {data.employees.map((employee) => <option key={employee.id} value={employee.id}>{employee.name}{employee.account ? ` — ${employee.account}` : ""}</option>)}
                </select>
              </div>
              <div className="field"><label>Employee name</label><input className="input" value={form.employeeName} onChange={set("employeeName")} /></div>
              <div className="field"><label>CNIC</label><input className="input" value={form.employeeCnic} onChange={set("employeeCnic")} /></div>
              <div className="field"><label>Bank</label><input className="input" value={form.employeeBank} onChange={set("employeeBank")} /></div>
              <div className="field"><label>Account number</label><input className="input" value={form.employeeAccount} onChange={set("employeeAccount")} /></div>
              <div className="field"><label>Phone</label><input className="input" value={form.employeePhone} onChange={set("employeePhone")} /></div>
              <div className="field field-wide"><label>Note</label><input className="input" value={form.note} onChange={set("note")} /></div>
            </div>
            <div className="button-row" style={{ marginTop: 16 }}>
              <button className="btn btn-primary" type="submit">Save Withdrawal</button>
              <button className="btn btn-muted" type="button" onClick={() => setMode("")}>Cancel</button>
            </div>
          </form>
        )}

        {mode === "upload" && (
          <div className="panel-body">
            <div className="form-grid">
              <PortalSelect value={uploadPortal} onChange={setUploadPortal} label="Import into portal" />
              <label className="file-drop"><div className="file-title">Choose the bank's invoice sheet (.xlsx)</div><div className="file-copy">Columns: id, date, person_name, cnic, AccountNumber, BankName, phoneNo, Amount, Rate … Repayment Date. Already-recorded transaction IDs are skipped.</div><input className="hidden-input" type="file" accept=".xlsx,.xls" onChange={chooseFile} /></label>
            </div>
            {parsed && (
              <>
                {parsed.errors.length > 0 && <div className="management-alert error">{parsed.errors.join(" ")} Fix the sheet and upload again — nothing is imported while rows have problems.</div>}
                <div className="table-wrap" style={{ marginTop: 12 }}>
                  <table className="data-table">
                    <thead><tr><th>Date</th><th>Employee</th><th>Bank / account</th><th>Amount</th><th>Days</th><th>Bank markup</th><th>Our markup</th><th>Check</th><th>Import</th></tr></thead>
                    <tbody>
                      {previewRows.map(({ row, duplicate, check }) => (
                        <tr key={row.bankTxnId} style={duplicate ? { opacity: 0.55 } : undefined}>
                          <td>{dateLabel(row.date)}</td><td>{row.employeeName}</td><td>{[row.employeeBank, row.employeeAccount].filter(Boolean).join(" · ")}</td>
                          <td>{money(row.amount)}</td>
                          <td>{check ? check.days : "—"}</td><td>{check ? money(check.bankMarkup) : "—"}</td><td>{check ? money(check.ourMarkup) : "—"}</td>
                          <td>{check ? <span className={`status-pill ${check.match ? "success" : "danger"}`}>{check.match ? "Matches" : `Differs by ${money(check.difference)}`}</span> : "—"}</td>
                          <td>{duplicate ? "Already recorded" : "New"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="page-copy">{newRows.length} new withdrawal(s), {money(newRows.reduce((sum, item) => sum + item.row.amount, 0))}. Interest figures are the bank's calculation to its repayment date; actual interest is recalculated from the date you repay.</p>
                <button type="button" className="btn btn-primary" disabled={busy || parsed.errors.length > 0 || !newRows.length} onClick={importNow}>Import {newRows.length} Withdrawal(s)</button>
              </>
            )}
          </div>
        )}

        <div className="panel-body table-wrap">
          <table className="data-table">
            <thead><tr><th>Date / time</th><th>Portal</th><th>Bank txn</th><th>Employee account</th><th>Amount</th><th>Returned</th><th>Charges</th><th>With employee</th><th>Outstanding</th><th>Interest to date</th><th>Reconciliation</th><th>Bank invoice</th><th /></tr></thead>
            <tbody>
              {position.rows.map((row) => {
                const w = row.withdrawal;
                const check = compareBankInvoice(w);
                return (
                  <tr key={w.id}>
                    <td>{dateLabel(w.date, w.time)}</td><td>{w.portal}</td>
                    <td style={{ fontSize: 12 }}>{w.bankTxnId ? `${w.bankTxnId.slice(0, 8)}…` : "—"}</td>
                    <td>{w.employeeName}<div className="page-copy" style={{ fontSize: 12 }}>{[w.employeeBank, w.employeeAccount].filter(Boolean).join(" · ")}</div></td>
                    <td>{money(row.principal)}</td><td>{money(row.returned)}</td><td>{money(row.charges)}</td><td>{money(row.withEmployee)}</td>
                    <td>{money(row.outstanding)}</td><td>{money(row.accruedInterest)}</td>
                    <td><span className={`status-pill ${RECON_TONE[row.reconStatus]}`}>{RECON_LABELS[row.reconStatus]}</span></td>
                    <td>{check ? <span className={`status-pill ${check.match ? "success" : "danger"}`}>{check.match ? "Matches" : `Differs ${money(check.difference)}`}</span> : "—"}</td>
                    <td><button className="btn btn-danger" type="button" onClick={() => reject(row)}>Reject</button></td>
                  </tr>
                );
              })}
              {!position.rows.length && <tr><td colSpan={13}>No withdrawals yet. Record one or upload the bank's sheet.</td></tr>}
            </tbody>
          </table>
        </div>
      </section>
    </>
  );
}

